/**
 * Per-flow binary draft store, backed by IndexedDB.
 *
 * Flows defer the R2 upload until "Generate" (so anonymous visitors don't burn
 * our bandwidth), which means before the auth/credit wall the uploaded image
 * lives only as an in-memory `File` + a `blob:` URL. Neither survives a page
 * reload, a new tab (email-confirm), or a navigation to /pricing.
 *
 * IndexedDB — not localStorage — because: it survives tab/restart, is shared
 * across tabs of the same origin, and has no ~5 MB quota cliff (a single phone
 * photo is 5–10 MB once base64-encoded). This module is React-free and is the
 * ONLY place that talks to IndexedDB; flows never import it directly — the
 * `useFlowPersistence` hook does.
 *
 * Safari private mode (and any environment where IndexedDB throws) degrades to
 * a silent no-op: reads return null, writes do nothing. Per the no-fallbacks
 * policy a miss shows the empty flow, never a fabricated state.
 */

const DB_NAME = "flowly-image-drafts";
const DB_VERSION = 1;
const STORE = "images";
const FLOW_KEY_INDEX = "flowKey";

/** Drafts older than this are treated as expired and dropped on read. */
export const FLOW_IMAGE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * One stored image slot. We persist the raw bytes plus name/type rather than
 * the `File` object itself: structured-clone of `File` is lossy in some
 * environments (it can come back as a nameless `Blob`), so we always rebuild
 * the `File` on read to guarantee name/type fidelity. `order` preserves
 * multi-image array ordering.
 */
interface ImageRecord {
  /** `${flowKey}::${slotId}` — the primary key. */
  id: string;
  flowKey: string;
  slotId: string;
  order: number;
  blob: Blob;
  fileName: string;
  fileType: string;
  storedAt: number;
}

/** A single image to persist / restore, identified by a stable slot id. */
export interface FlowImageEntry {
  id: string;
  file: File;
}

const recordId = (flowKey: string, slotId: string) => `${flowKey}::${slotId}`;

/**
 * One open-DB promise per IDBFactory. Production uses the global factory;
 * tests inject `fake-indexeddb` and get an isolated cached handle. A failed
 * open resolves to `null` and stays cached so we don't retry every call.
 */
const dbCache = new WeakMap<IDBFactory, Promise<IDBDatabase | null>>();

function openDB(factory: IDBFactory): Promise<IDBDatabase | null> {
  const cached = dbCache.get(factory);
  if (cached) return cached;

  const promise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      const req = factory.open(DB_NAME, DB_VERSION);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "id" });
          store.createIndex(FLOW_KEY_INDEX, "flowKey", { unique: false });
        }
      };
    } catch {
      resolve(null);
    }
  });

  dbCache.set(factory, promise);
  return promise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
    tx.onabort = () => resolve();
  });
}

/**
 * Read every primary key stored under `flowKey`, in its own readonly
 * transaction. Kept separate from the write below: you must NEVER `await`
 * between two operations on the same IndexedDB transaction — the tx
 * auto-commits/deactivates during the await and the next op throws
 * `TransactionInactiveError`. (A bare key is an exact-match query — no
 * `IDBKeyRange`, which isn't global in every JS env.)
 */
function readKeysForFlow(
  db: IDBDatabase,
  flowKey: string,
): Promise<IDBValidKey[]> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).index(FLOW_KEY_INDEX).getAllKeys(flowKey);
      req.onsuccess = () => resolve((req.result as IDBValidKey[]) ?? []);
      req.onerror = () => resolve([]);
      tx.onabort = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

/**
 * Atomically replace all images stored under `flowKey` with `entries`.
 * Passing an empty array clears the key.
 */
export async function putFlowImages(
  flowKey: string,
  entries: FlowImageEntry[],
  factory: IDBFactory = globalThis.indexedDB,
): Promise<void> {
  if (!factory) return;
  const db = await openDB(factory);
  if (!db) return;

  // Phase 1: which keys currently exist for this flowKey (separate readonly
  // tx — see readKeysForFlow re: the no-await-mid-transaction rule).
  const stale = await readKeysForFlow(db, flowKey);

  // Phase 2: atomic replace. Delete the stale keys + put the new records, all
  // issued SYNCHRONOUSLY within one readwrite tx (no `await` between ops, or
  // the tx commits mid-flight and `store.put` throws TransactionInactiveError
  // in real browsers — the original silent-image-loss bug).
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
      for (const key of stale) store.delete(key);
      const storedAt = Date.now();
      entries.forEach((entry, order) => {
        const record: ImageRecord = {
          id: recordId(flowKey, entry.id),
          flowKey,
          slotId: entry.id,
          order,
          blob: entry.file.slice(0, entry.file.size, entry.file.type),
          fileName: entry.file.name,
          fileType: entry.file.type,
          storedAt,
        };
        store.put(record);
      });
    } catch {
      resolve();
    }
  });
}

/**
 * Return the images stored under `flowKey` in their original order, or `null`
 * if there are none or the draft has expired (TTL). Expired drafts self-clear.
 */
export async function getFlowImages(
  flowKey: string,
  factory: IDBFactory = globalThis.indexedDB,
): Promise<FlowImageEntry[] | null> {
  if (!factory) return null;
  const db = await openDB(factory);
  if (!db) return null;

  const records = await new Promise<ImageRecord[]>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).index(FLOW_KEY_INDEX).getAll(flowKey);
      req.onsuccess = () => resolve((req.result as ImageRecord[]) ?? []);
      req.onerror = () => resolve([]);
      tx.onabort = () => resolve([]);
    } catch {
      resolve([]);
    }
  });

  if (records.length === 0) return null;

  const expired = records.some(
    (r) => Date.now() - r.storedAt > FLOW_IMAGE_TTL_MS,
  );
  if (expired) {
    await clearFlowImages(flowKey, factory);
    return null;
  }

  return records
    .sort((a, b) => a.order - b.order)
    .map((r) => ({
      id: r.slotId,
      file: new File([r.blob], r.fileName, { type: r.fileType }),
    }));
}

/** Drop every image stored under `flowKey`. */
export async function clearFlowImages(
  flowKey: string,
  factory: IDBFactory = globalThis.indexedDB,
): Promise<void> {
  if (!factory) return;
  const db = await openDB(factory);
  if (!db) return;
  // Cursor-driven delete: each delete + continue is issued inside the cursor's
  // onsuccess, keeping the tx continuously alive — no mid-tx `await`.
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
      const cursorReq = store.index(FLOW_KEY_INDEX).openKeyCursor(flowKey);
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          store.delete(cursor.primaryKey);
          cursor.continue();
        }
      };
      cursorReq.onerror = () => {};
    } catch {
      resolve();
    }
  });
}

/** Wipe the entire store — used on sign-out so drafts don't leak across users. */
export async function clearAllFlowImages(
  factory: IDBFactory = globalThis.indexedDB,
): Promise<void> {
  if (!factory) return;
  const db = await openDB(factory);
  if (!db) return;
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).clear();
  await txDone(tx);
}
